import React, { useContext, useState, useRef, useEffect } from "react";
import Ctx from "../../contexts/AppContext";
import { api } from "../../api";
import { crd, bdg, btnP, btnSec, sp, rad } from "../../constants/styles";
import Ic from "../common/Ic";
import PropertyCondition from "../common/PropertyCondition";
import { getLeaseStatus, getDaysRemaining, isExpiringSoon, leaseStatusColor, leaseStatusLabel, getViewableLeaseForTenant } from "../../utils/leaseHelpers";
import { formatPropAddr } from "../../utils/propertyAddr";
import { defaultTerms, generateAgreementHTML, buildAgreementDataFromLease, buildSigHTML } from "../../utils/agreement";

function TenLeasePage() {
  const { t, h, leases, setLeases, tens, setTens, props, currUserId, go, flash } = useContext(Ctx);
  const me = tens.find(x => x.id === currUserId);
  // Show the active/draft lease when present, else fall back to the most-recent
  // (e.g. "terminated" after move-out) so a former tenant sees their PAST
  // agreement instead of "No agreement yet".
  const lease = getViewableLeaseForTenant(leases, currUserId);
  const myProp = props.find(x => x.id === (lease?.property_id || me?.pid));
  const status = getLeaseStatus(lease);
  const days = getDaysRemaining(lease);
  const expSoon = isExpiringSoon(lease);
  // Read-only (former / moved-out tenant): agreement shown for reference only,
  // no signing; surfaces the deposit-settlement receipt instead.
  const isFormer = (() => { try { return localStorage.getItem("formerTenant") === "true"; } catch (_) { return false; } })();
  const readOnly = isFormer || !!me?.detached_at || status === "terminated";
  const [showSign, setShowSign] = useState(false);
  const [signing, setSigning] = useState(false);
  const [showClauses, setShowClauses] = useState(false);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  // In-app agreement viewer (read-only iframe + download) — mirrors the
  // landlord's "View Agreement" experience.
  const [showDoc, setShowDoc] = useState(false);
  const [docHtml, setDocHtml] = useState("");
  // Deposit-settlement receipt for a terminated lease (former-tenant view).
  // Returns { settlement, deductions, ... } when move-out went through the
  // vacate flow; stays null otherwise (e.g. lease ended outside the flow).
  const [settlement, setSettlement] = useState(null);
  useEffect(() => {
    if (!readOnly || !lease?.id) { setSettlement(null); return; }
    let alive = true;
    api.vacateSettlementByLease(lease.id)
      .then(r => { if (alive && r && r.success && r.settlement) setSettlement(r); })
      .catch(() => {});
    return () => { alive = false; };
  }, [readOnly, lease?.id]);

  // Parse terms from lease
  const parseTerms = () => { try { return typeof lease?.terms === 'string' ? JSON.parse(lease.terms) : (lease?.terms || {}); } catch { return {}; } };
  const terms = { ...defaultTerms, ...parseTerms() };

  // Signature pad handlers
  const startDraw = (e) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.beginPath(); ctx.moveTo(x, y);
  };
  const draw = (e) => {
    if (!isDrawing) return; e.preventDefault();
    const canvas = canvasRef.current; const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.strokeStyle = h.tx; ctx.lineWidth = 2; ctx.lineCap = "round";
    ctx.lineTo(x, y); ctx.stroke();
  };
  const endDraw = () => setIsDrawing(false);
  const clearSign = () => { const c = canvasRef.current; if (c) c.getContext("2d").clearRect(0, 0, c.width, c.height); };

  const submitSign = async () => {
    const canvas = canvasRef.current; if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    const blank = document.createElement("canvas"); blank.width = canvas.width; blank.height = canvas.height;
    if (canvas.toDataURL() === blank.toDataURL()) { flash("Please draw your signature first", "error"); return; }
    setSigning(true);
    try {
      // Persist the signature image via the tenant-self endpoint. The
      // generic POST /tenants is landlord-only and used to silently 403
      // here \u2014 the local "Signed" state appeared but the DB row never
      // updated, so the landlord saw nothing on their next sync.
      const updatedMe = { ...me, esign: dataUrl };
      setTens(tens.map(x => x.id === me.id ? updatedMe : x));
      const sig = await api.saveTenantEsign(dataUrl);
      if (sig && sig.success === false) {
        // Roll the local state back so the UI doesn't lie about a save
        // that didn't land.
        setTens(tens.map(x => x.id === me.id ? me : x));
        flash(sig.message || "Failed to save signature", "error");
        setSigning(false);
        return;
      }
      if (lease) {
        const updatedLease = { ...lease, status: "active", signed_at: new Date().toISOString() };
        setLeases(leases.map(l => l.id === lease.id ? updatedLease : l));
        await api.saveLease(updatedLease);
      }
      setShowSign(false);
      flash("Agreement signed successfully! \u2705");
    } catch (e) { flash("Failed to save signature", "error"); }
    setSigning(false);
  };

  // PDF
  // Open the agreement HTML via a Blob URL rather than window.open +
  // document.write. The latter is treated as a popup by mobile browsers
  // and Capacitor's WebView and gets blocked silently — the user clicks
  // and nothing happens. Blob URLs go through the same channel as a
  // normal link click, which the popup blockers leave alone. Same fix
  // the landlord flow shipped in commit d1bd891.
  // Build the agreement HTML once — shared by the in-app viewer and the
  // download path so "what you see = what downloads".
  const buildHtml = () => {
    if (!lease) return null;
    const data = buildAgreementDataFromLease(lease, me, myProp);
    if (!data) return null;
    // Use the language list the landlord picked on the lease itself, so
    // the tenant's copy mirrors what the landlord intended. Falls through
    // to the default ['en','ta'] inside generateAgreementHTML when no
    // selection has been saved yet.
    const langs = Array.isArray(terms.pdf_languages) && terms.pdf_languages.length > 0
      ? terms.pdf_languages
      : undefined;
    return generateAgreementHTML(data, langs);
  };

  // Open the read-only in-app document viewer.
  const viewAgreement = () => {
    const html = buildHtml();
    if (!html) { flash("Missing data", "error"); return; }
    setDocHtml(html);
    setShowDoc(true);
  };

  const printPDF = () => {
    const html = buildHtml();
    if (!html) { flash("Missing data", "error"); return; }
    try {
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank', 'noopener,noreferrer');
      if (!w) {
        // Last-resort: trigger a real anchor click. Capacitor / mobile
        // Safari treat this as a navigation, not a popup.
        const a = document.createElement('a');
        a.href = url; a.target = '_blank'; a.rel = 'noopener noreferrer';
        document.body.appendChild(a); a.click(); a.remove();
      }
      // Revoke after a beat so the new tab has time to load.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      flash("Could not open agreement — try again", "error");
    }
  };

  // In-app document viewer — read-only iframe + Download, mirroring the
  // landlord's "View Agreement". srcDoc loads the exact HTML the PDF uses,
  // so what the tenant sees is what downloads.
  if (showDoc && lease) return <div style={{ paddingTop: 16, display: "flex", flexDirection: "column", height: "calc(100vh - 120px)" }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, gap: 8 }}>
      <button onClick={() => setShowDoc(false)} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>{"←"} {t.back}</button>
      <button onClick={printPDF} style={{ background: h.ac, border: "none", color: "#fff", padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{"⬇️"} {t.download || "Download"}</button>
    </div>
    <div style={{ flex: 1, borderRadius: 12, overflow: "hidden", border: `1px solid ${h.bd}`, background: "#fff" }}>
      <iframe title="Rental Agreement" srcDoc={docHtml} sandbox="allow-same-origin" style={{ width: "100%", height: "100%", border: "none", display: "block" }} />
    </div>
  </div>;

  // No lease
  if (!lease) return <div style={{ paddingTop: 16 }}>
    <button onClick={() => go("dash")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: 8 }}>&larr; {t.back}</button>
    <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, margin: "0 0 16px" }}>My Agreement</h2>
    <div style={{ ...crd(h), textAlign: "center", padding: 24 }}>
      <Ic n="file" s={32} c={h.sc} />
      <div style={{ color: h.sc, fontSize: 14, marginTop: 10 }}>No agreement yet</div>
      {me?.pid && myProp && <div style={{ marginTop: 14, borderTop: `1px solid ${h.bd}`, paddingTop: 14 }}>
        <div style={{ color: h.tx, fontSize: 13, fontWeight: 600 }}>{formatPropAddr(myProp)}</div>
        <div style={{ color: h.sc, fontSize: 11, marginTop: 4 }}>{myProp.rooms} rooms \u00B7 \u20B9{myProp.rent?.toLocaleString()}/mo</div>
      </div>}
    </div>
  </div>;

  const row = (label, val) => <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${h.bd}30` }}>
    <span style={{ color: h.sc, fontSize: 12 }}>{label}</span>
    <span style={{ color: h.tx, fontSize: 13, fontWeight: 600 }}>{val}</span>
  </div>;

  const isDraft = status === "draft";
  const isSigned = !!lease.signed_at;
  const llSig = terms.landlord_signature;

  return <div style={{ paddingTop: 16 }}>
    <button onClick={() => go("dash")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: 8 }}>&larr; {t.back}</button>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
      <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, margin: 0 }}>My Agreement</h2>
      <span style={bdg(`${leaseStatusColor(status, h)}20`, leaseStatusColor(status, h))}>{leaseStatusLabel(status, t)}</span>
    </div>

    {/* Draft Banner */}
    {isDraft && !showSign && <div style={{ background: `${h.ac}10`, borderRadius: 12, padding: 14, marginBottom: 12, border: `1px solid ${h.ac}30` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 16 }}>{"\uD83D\uDCCB"}</span>
        <span style={{ color: h.ac, fontWeight: 700, fontSize: 13 }}>Agreement Ready for Review</span>
      </div>
      <div style={{ color: h.sc, fontSize: 11, lineHeight: 1.5, marginBottom: 10 }}>Your landlord has created an agreement. Review the terms below and sign to activate.</div>
      <button onClick={() => setShowSign(true)} style={{ ...btnP(h), width: "100%", padding: "10px 16px" }}>{"\u270D\uFE0F"} Review & Sign</button>
    </div>}

    {/* Signed confirmation (active tenancy only) */}
    {isSigned && !readOnly && <div style={{ background: `${h.gn}10`, borderRadius: 12, padding: 14, marginBottom: 12, border: `1px solid ${h.gn}30` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 16 }}>{"\u2705"}</span>
        <span style={{ color: h.gn, fontWeight: 700, fontSize: 13 }}>Agreement Signed & Active</span>
      </div>
      <div style={{ color: h.sc, fontSize: 11, marginTop: 4 }}>Signed on {new Date(lease.signed_at).toLocaleDateString("en-IN")}</div>
    </div>}

    {expSoon && <div style={{ background: `${h.wn}15`, borderRadius: 10, padding: "10px 14px", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 14 }}>{"\u26A0\uFE0F"}</span>
      <span style={{ color: h.wn, fontSize: 12, fontWeight: 600 }}>{days} days remaining</span>
    </div>}

    {/* Former tenant: past-agreement notice */}
    {readOnly && <div style={{ background: `${h.ac}08`, borderRadius: 12, padding: 14, marginBottom: 12, border: `1px solid ${h.bd}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 16 }}>{"📁"}</span>
        <span style={{ color: h.tx, fontWeight: 700, fontSize: 13 }}>Past Agreement (read-only)</span>
      </div>
      <div style={{ color: h.sc, fontSize: 11, marginTop: 4, lineHeight: 1.5 }}>This tenancy has ended. Your agreement and move-in photos are kept here for your records.</div>
    </div>}

    {/* Former tenant: deposit-settlement receipt (taps through to the
        full move-out photos + downloadable receipt on the vacate page) */}
    {readOnly && settlement?.settlement && (() => {
      const s = settlement.settlement;
      const fmt = (paise) => "₹" + Math.round(Number(paise) / 100).toLocaleString("en-IN");
      return <div onClick={() => go("tenVacate")} style={{ ...crd(h), padding: 14, marginBottom: 12, background: `${h.gn}08`, border: `1px solid ${h.gn}40`, cursor: "pointer" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={{ color: h.gn, fontWeight: 700, fontSize: 13 }}>{"🧾"} Move-out Settlement</span>
          <span style={{ color: h.ac, fontSize: 11, fontWeight: 600 }}>Photos & receipt {"→"}</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: h.sc, marginBottom: 3 }}><span>Deposit</span><span>{fmt(s.deposit_amount)}</span></div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: h.rd, marginBottom: 3 }}><span>Deductions</span><span>-{fmt(s.total_deductions)}</span></div>
        <div style={{ height: 1, background: h.bd, margin: "6px 0" }} />
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, fontWeight: 700 }}><span style={{ color: h.tx }}>Refund</span><span style={{ color: h.gn }}>{fmt(s.refund_amount)}</span></div>
        {s.settled_at && <div style={{ color: h.ts, fontSize: 9, marginTop: 6 }}>Settled on {new Date(s.settled_at).toLocaleDateString("en-IN")}</div>}
      </div>;
    })()}

    {/* Property Info */}
    <div style={crd(h)}>
      <div style={{ color: h.ac, fontWeight: 600, fontSize: 12, marginBottom: 6 }}>Property</div>
      <div style={{ color: h.tx, fontSize: 14, fontWeight: 600 }}>{myProp ? formatPropAddr(myProp) : "\u2014"}</div>
      {myProp?.rooms && <div style={{ color: h.sc, fontSize: 11, marginTop: 4 }}>{myProp.rooms} rooms</div>}
    </div>

    {/* Lease Terms */}
    <div style={crd(h)}>
      <div style={{ color: h.ac, fontWeight: 600, fontSize: 12, marginBottom: 6 }}>Lease Terms</div>
      {row("Start Date", lease.start_date || "\u2014")}
      {row("End Date", lease.end_date || "\u2014")}
      {row("Days Remaining", days != null ? days : "\u2014")}
      {row("Rent", `\u20B9${Number(lease.rent || 0).toLocaleString()}/mo`)}
      {row("Deposit", `\u20B9${Number(lease.deposit || 0).toLocaleString()}`)}
      {row("Notice Period", `${lease.notice_period_days || 30} days`)}
      {lease.lock_in_months && row("Lock-in", `${lease.lock_in_months} months`)}
      {lease.escalation_pct && row("Escalation", `${lease.escalation_pct}% annually`)}
    </div>

    {/* Agreement Clauses (collapsible) */}
    <div style={crd(h)}>
      <button onClick={() => setShowClauses(!showClauses)} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 4, padding: 0 }}>
        {showClauses ? "\u25BE" : "\u25B8"} Agreement Clauses & Terms
      </button>
      {showClauses && <div style={{ marginTop: 10 }}>
        {terms.maintenance_clause && <div style={{ marginBottom: 8 }}><div style={{ color: h.tx, fontSize: 11, fontWeight: 600 }}>Maintenance</div><div style={{ color: h.sc, fontSize: 11, lineHeight: 1.5 }}>{terms.maintenance_clause}</div></div>}
        {terms.usage_clause && <div style={{ marginBottom: 8 }}><div style={{ color: h.tx, fontSize: 11, fontWeight: 600 }}>Usage</div><div style={{ color: h.sc, fontSize: 11, lineHeight: 1.5 }}>{terms.usage_clause}</div></div>}
        {terms.electricity_clause && <div style={{ marginBottom: 8 }}><div style={{ color: h.tx, fontSize: 11, fontWeight: 600 }}>Electricity/Water</div><div style={{ color: h.sc, fontSize: 11, lineHeight: 1.5 }}>{terms.electricity_clause}</div></div>}
        {terms.fittings && <div style={{ marginBottom: 8 }}><div style={{ color: h.tx, fontSize: 11, fontWeight: 600 }}>Fittings</div><div style={{ color: h.sc, fontSize: 11 }}>{terms.fittings}</div></div>}
        {terms.property_tax_clause && <div style={{ marginBottom: 8 }}><div style={{ color: h.tx, fontSize: 11, fontWeight: 600 }}>Property Tax</div><div style={{ color: h.sc, fontSize: 11, lineHeight: 1.5 }}>{terms.property_tax_clause}</div></div>}
        {terms.painting_clause && <div style={{ marginBottom: 8 }}><div style={{ color: h.tx, fontSize: 11, fontWeight: 600 }}>Painting/Repair</div><div style={{ color: h.sc, fontSize: 11, lineHeight: 1.5 }}>{terms.painting_clause}</div></div>}
        {terms.special_terms && <div style={{ marginBottom: 8 }}><div style={{ color: h.tx, fontSize: 11, fontWeight: 600 }}>Special Terms</div><div style={{ color: h.sc, fontSize: 11, lineHeight: 1.5 }}>{terms.special_terms}</div></div>}
        {/* Custom clauses */}
        {(terms.custom_clauses || []).map((cc, i) => cc.title || cc.text ? <div key={`cc_${i}`} style={{ marginBottom: 8 }}>
          <div style={{ color: h.tx, fontSize: 11, fontWeight: 600 }}>{cc.title || `Additional Clause ${i + 1}`}</div>
          <div style={{ color: h.sc, fontSize: 11, lineHeight: 1.5 }}>{cc.text}</div>
        </div> : null)}
      </div>}
    </div>

    {/* Property Condition — read-only for the tenant, but visible BEFORE
        signing: this is the record they will be measured against at
        move-out, so they must be able to review it up front. */}
    <PropertyCondition
      lease={lease}
      editable={false}
      addedBy="tenant"
      onSave={async () => { /* no-op: tenant view is read-only */ }}
    />

    {/* Signatures */}
    <div style={crd(h)}>
      <div style={{ color: h.ac, fontWeight: 600, fontSize: 12, marginBottom: 8 }}>Signatures</div>
      {llSig ? <div style={{ background: `${h.gn}10`, padding: 10, borderRadius: 8, border: `1px solid ${h.gn}25`, marginBottom: 8 }}>
        <div style={{ color: h.gn, fontSize: 11, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>{"\u2713"} Landlord Signed</div>
        {llSig.startsWith("sig|") && <img src={llSig.split("|").slice(2).join("|")} alt="sig" style={{ maxHeight: 50, background: "#fff", padding: 4, borderRadius: 6, display: "block" }} />}
      </div> : <div style={{ color: h.wn, fontSize: 11, marginBottom: 8 }}>{"\u23F3"} Awaiting landlord signature</div>}

      {me?.esign ? <div>
        <div style={{ background: `${h.gn}10`, padding: 10, borderRadius: 8, border: `1px solid ${h.gn}25` }}>
          <div style={{ color: h.gn, fontSize: 11, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>{"\u2713"} You Signed</div>
          <img src={me.esign.startsWith("sig|") ? me.esign.split("|").slice(2).join("|") : me.esign} alt="sig" style={{ maxHeight: 50, background: "#fff", padding: 4, borderRadius: 6, display: "block" }} />
          {me.esign.startsWith("sig|") && <div style={{ color: h.sc, fontSize: 9, marginTop: 2 }}>Date: {me.esign.split("|")[1]}</div>}
        </div>
        {!readOnly && <button onClick={() => setShowSign(true)} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 11, fontWeight: 600, padding: 0, marginTop: 4 }}>{"\u270F\uFE0F"} Re-Sign</button>}
      </div> : (!readOnly && !showSign && <button onClick={() => setShowSign(true)} style={{ ...btnP(h), width: "100%", padding: 10 }}>{"\u270D\uFE0F"} Sign Agreement</button>)}
    </div>

    {/* E-Signature Pad */}
    {showSign && <div style={{ ...crd(h), border: `2px solid ${h.ac}40`, padding: 16 }}>
      <div style={{ color: h.ac, fontWeight: 700, fontSize: 14, marginBottom: 10 }}>{"\u270D\uFE0F"} E-Sign Agreement</div>
      <div style={{ color: h.sc, fontSize: 11, marginBottom: 8, lineHeight: 1.5 }}>By signing below, I agree to the terms shown above. This e-signature is legally binding under the Indian IT Act, 2000.</div>
      <div style={{ border: `1px solid ${h.bd}`, borderRadius: 10, marginBottom: 8, background: h.bg, overflow: "hidden" }}>
        <canvas ref={canvasRef} width={360} height={120} style={{ width: "100%", height: 120, touchAction: "none", cursor: "crosshair" }}
          onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw}
          onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw} />
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={clearSign} style={{ ...btnSec(h), flex: 1 }}>Clear</button>
        <button onClick={() => setShowSign(false)} style={{ ...btnSec(h), flex: 1 }}>Cancel</button>
        <button onClick={submitSign} disabled={signing} style={{ ...btnP(h), flex: 2, opacity: signing ? 0.6 : 1 }}>{signing ? "Saving..." : "\u2705 Sign & Activate"}</button>
      </div>
    </div>}

    {/* View full agreement in-app, then download from the viewer. A
        direct download shortcut stays below for convenience. */}
    <button onClick={viewAgreement} style={{ ...btnP(h), marginTop: 4, width: "100%" }}>{"\uD83D\uDCDC"} View Agreement</button>
    <button onClick={printPDF} style={{ ...btnSec(h), marginTop: 8, marginBottom: 24, width: "100%" }}>{"\uD83D\uDCC4"} Download Agreement PDF</button>
  </div>;
}

export default TenLeasePage;
